import { organisationsService } from '@/services/organisations.service';
import { useOrganisationsStore } from '@/store/organisations.store';
import { useAuthStore } from '@/store/auth.store';
import {
  CreateOrganisationDto,
  FolderScope,
  InviteMemberDto,
  OrganisationRole,
  RegisterFromOrgInvitationData,
  UpdateOrganisationDto,
} from '@/types/organisation.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

export const organisationKeys = {
  all: ['organisations'] as const,
  list: () => ['organisations', 'list'] as const,
  detail: (uuid: string) => ['organisations', uuid] as const,
  members: (uuid: string) => ['organisations', uuid, 'members'] as const,
  responsables: (uuid: string) =>
    ['organisations', uuid, 'responsables'] as const,
  invitations: (uuid: string) =>
    ['organisations', uuid, 'invitations'] as const,
  invitation: (token: string) =>
    ['organisations', 'invitation', token] as const,
  folderAccess: (folderUuid: string) =>
    ['folders', folderUuid, 'access'] as const,
};

export const useOrganisations = () => {
  const { setOrganisations, setLoading } = useOrganisationsStore();

  const query = useQuery({
    queryKey: organisationKeys.list(),
    queryFn: () => organisationsService.getOrganisations(),
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    setLoading(query.isLoading);
    if (query.data) {
      setOrganisations(query.data);
    }
  }, [query.data, query.isLoading, setOrganisations, setLoading]);

  return query;
};

export const useOrganisationDetail = (uuid: string) => {
  return useQuery({
    queryKey: organisationKeys.detail(uuid),
    queryFn: () => organisationsService.getOrganisation(uuid),
    enabled: !!uuid,
  });
};

export const useCreateOrganisation = () => {
  const queryClient = useQueryClient();
  const { setCurrentOrganisation } = useOrganisationsStore();

  return useMutation({
    mutationFn: (dto: CreateOrganisationDto) =>
      organisationsService.createOrganisation(dto),
    onSuccess: org => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.all });
      setCurrentOrganisation(org);
      toast.success('Organisation créée avec succès');
    },
    onError: () => {
      toast.error("Erreur lors de la création de l'organisation");
    },
  });
};

export const useUpdateOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uuid,
      data,
    }: {
      uuid: string;
      data: UpdateOrganisationDto;
    }) => organisationsService.updateOrganisation(uuid, data),
    onSuccess: (_, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: organisationKeys.detail(uuid),
      });
      queryClient.invalidateQueries({ queryKey: organisationKeys.list() });
      toast.success('Organisation mise à jour');
    },
    onError: () => {
      toast.error("Erreur lors de la mise à jour de l'organisation");
    },
  });
};

export const useOrganisationMembers = (uuid: string) => {
  return useQuery({
    queryKey: organisationKeys.members(uuid),
    queryFn: () => organisationsService.getMembers(uuid),
    enabled: !!uuid,
  });
};

export const useInviteMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: InviteMemberDto }) =>
      organisationsService.inviteMember(uuid, data),
    onSuccess: (_, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: organisationKeys.members(uuid),
      });
      queryClient.invalidateQueries({
        queryKey: organisationKeys.invitations(uuid),
      });
      toast.success('Invitation envoyée avec succès');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message || "Erreur lors de l'invitation";
      toast.error(message);
    },
  });
};

/**
 * Met à jour un membre : rôle et/ou cloisonnement des dossiers
 * (folderScope + responsableAliases). Tous les champs sont optionnels —
 * on n'envoie que ce qui change.
 */
export const useUpdateMemberRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uuid,
      memberUuid,
      role,
      folderScope,
      responsableAliases,
    }: {
      uuid: string;
      memberUuid: string;
      role?: OrganisationRole;
      folderScope?: FolderScope;
      responsableAliases?: string[];
    }) =>
      organisationsService.updateMemberRole(uuid, memberUuid, {
        ...(role !== undefined ? { role } : {}),
        ...(folderScope !== undefined ? { folderScope } : {}),
        ...(responsableAliases !== undefined ? { responsableAliases } : {}),
      }),
    onSuccess: (_, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: organisationKeys.members(uuid),
      });
      toast.success('Membre mis à jour');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        'Erreur lors de la mise à jour du membre';
      toast.error(message);
    },
  });
};

/**
 * Liste des responsables distincts (valeurs responsable_dossier) sur les
 * domaines de l'organisation, avec le nombre de dossiers. Réservé admin (API).
 * Sert au multi-sélecteur d'alias dans les réglages d'un membre.
 */
export const useOrganisationResponsables = (
  orgUuid: string,
  enabled = true
) => {
  return useQuery({
    queryKey: organisationKeys.responsables(orgUuid),
    queryFn: () => organisationsService.getResponsables(orgUuid),
    enabled: !!orgUuid && enabled,
    staleTime: 10 * 60 * 1000,
  });
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, memberUuid }: { uuid: string; memberUuid: string }) =>
      organisationsService.removeMember(uuid, memberUuid),
    onSuccess: (_, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: organisationKeys.members(uuid),
      });
      toast.success('Membre retiré');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        'Erreur lors de la suppression du membre';
      toast.error(message);
    },
  });
};

export const useLeaveOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid: string) => organisationsService.leaveOrganisation(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.all });
      toast.success("Vous avez quitté l'organisation");
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        "Impossible de quitter l'organisation";
      toast.error(message);
    },
  });
};

// Archive hooks
export const useArchiveDomain = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      domainUuid,
      archive,
    }: {
      domainUuid: string;
      archive: boolean;
    }) => organisationsService.archiveDomain(domainUuid, archive),
    onSuccess: (_, { archive }) => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      queryClient.invalidateQueries({ queryKey: ['folder'] });
      toast.success(archive ? 'Domaine archivé' : 'Domaine désarchivé');
    },
    onError: () => {
      toast.error("Erreur lors de l'archivage");
    },
  });
};

export const useArchiveFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderUuid,
      archive,
    }: {
      folderUuid: string;
      archive: boolean;
    }) => organisationsService.archiveFolder(folderUuid, archive),
    onSuccess: (_, { folderUuid, archive }) => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      queryClient.invalidateQueries({
        queryKey: ['folder', 'detail', folderUuid],
      });
      toast.success(archive ? 'Dossier archivé' : 'Dossier désarchivé');
    },
    onError: () => {
      toast.error("Erreur lors de l'archivage du dossier");
    },
  });
};

// Restriction hooks
export const useRestrictFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderUuid,
      isRestricted,
    }: {
      folderUuid: string;
      isRestricted: boolean;
    }) => organisationsService.restrictFolder(folderUuid, isRestricted),
    onSuccess: (_, { folderUuid, isRestricted }) => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      queryClient.invalidateQueries({
        queryKey: ['folder', 'detail', folderUuid],
      });
      toast.success(
        isRestricted ? 'Accès au dossier restreint' : 'Accès au dossier libéré'
      );
    },
    onError: () => {
      toast.error("Erreur lors de la modification de l'accès");
    },
  });
};

export const useFolderAccess = (folderUuid: string) => {
  return useQuery({
    queryKey: organisationKeys.folderAccess(folderUuid),
    queryFn: () => organisationsService.getFolderAccess(folderUuid),
    enabled: !!folderUuid,
  });
};

export const useGrantFolderAccess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderUuid,
      memberUuid,
    }: {
      folderUuid: string;
      memberUuid: string;
    }) => organisationsService.grantFolderAccess(folderUuid, memberUuid),
    onSuccess: (_, { folderUuid }) => {
      queryClient.invalidateQueries({
        queryKey: organisationKeys.folderAccess(folderUuid),
      });
      toast.success('Accès accordé');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message || "Erreur lors de l'ajout de l'accès";
      toast.error(message);
    },
  });
};

export const useRevokeFolderAccess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderUuid,
      memberUuid,
    }: {
      folderUuid: string;
      memberUuid: string;
    }) => organisationsService.revokeFolderAccess(folderUuid, memberUuid),
    onSuccess: (_, { folderUuid }) => {
      queryClient.invalidateQueries({
        queryKey: organisationKeys.folderAccess(folderUuid),
      });
      toast.success('Accès révoqué');
    },
    onError: () => {
      toast.error("Erreur lors de la révocation de l'accès");
    },
  });
};

// --- Invitation hooks ---

export const useInvitationByToken = (token: string) => {
  return useQuery({
    queryKey: organisationKeys.invitation(token),
    queryFn: () => organisationsService.getInvitationByToken(token),
    enabled: !!token,
    retry: false,
  });
};

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => organisationsService.acceptInvitation(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.all });
      toast.success("Invitation acceptée ! Vous avez rejoint l'organisation.");
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        "Erreur lors de l'acceptation de l'invitation";
      toast.error(message);
    },
  });
};

/**
 * Inscription d'un nouveau collaborateur depuis une invitation d'organisation.
 * Crée le compte côté API, stocke les tokens comme un login normal (auto-login),
 * puis laisse l'appelant rediriger.
 */
export const useRegisterFromOrgInvitation = () => {
  const { setUser, setTokens } = useAuthStore();

  return useMutation({
    mutationFn: ({
      token,
      data,
    }: {
      token: string;
      data: RegisterFromOrgInvitationData;
    }) => organisationsService.registerFromInvitation(token, data),
    onSuccess: response => {
      setUser(response.user);
      setTokens(response.tokens);
      localStorage.setItem('accessToken', response.tokens.accessToken);
      localStorage.setItem('refreshToken', response.tokens.refreshToken);
      toast.success("Compte créé. Bienvenue dans l'organisation !");
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        'Erreur lors de la création du compte';
      toast.error(message);
    },
  });
};

export const usePendingInvitations = (uuid: string, enabled = true) => {
  return useQuery({
    queryKey: organisationKeys.invitations(uuid),
    queryFn: () => organisationsService.getPendingInvitations(uuid),
    enabled: !!uuid && enabled,
  });
};

export const useCancelInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uuid,
      invitationUuid,
    }: {
      uuid: string;
      invitationUuid: string;
    }) => organisationsService.cancelInvitation(uuid, invitationUuid),
    onSuccess: (_, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: organisationKeys.invitations(uuid),
      });
      toast.success('Invitation annulée');
    },
    onError: () => {
      toast.error("Erreur lors de l'annulation de l'invitation");
    },
  });
};
